import type { Dispatch } from '@reduxjs/toolkit';
import { autoRenewalBannerEnabled, unbindCardBannerEnabled } from 'reactApp/appHelpers/featuresHelpers/features/autoRenewalCommunication';
import type { BillingDataSubscriptionInfo } from 'reactApp/modules/billingData/billingData.types';
import { CardActions } from 'reactApp/modules/creditCard/creditCard.module';
import { CardSelectors } from 'reactApp/modules/creditCard/creditCard.selectors';
import type { ICard } from 'reactApp/modules/creditCard/creditCard.types';
import { showSnackbarAction } from 'reactApp/modules/snackbar/snackbar.actions';
import { SnackbarTypes } from 'reactApp/modules/snackbar/snackbar.types';
import { SubscriptionRenewalActions } from 'reactApp/modules/subscriptionRenewal/subscriptionRenewal.module';
import type { TSendAnalytics } from 'reactApp/sections/SubscriptionsPage/ui/SubscriptionModal/SubscriptionModal.analytics';
import { store } from 'reactApp/store';

import {
    getAutoRenewalEnabledFromHomeBannerSnackbarText,
    getAutoRenewalEnabledSnackbarText,
    getCardAddedAndAutoRenewalEnabledSnackbarText,
    getCardAddedSnackbarText,
} from './subscriptionCommunication.helpers';

type EnableAutoRenewParams = {
    subscription: BillingDataSubscriptionInfo;
    card?: ICard | null;
    sendAnalytics?: TSendAnalytics;
};

const showCommunicationSnackbar = (dispatch: Dispatch, { text, id }: { text: string; id: string }): void => {
    dispatch(
        showSnackbarAction({
            text,
            type: SnackbarTypes.success,
            id,
            closable: false,
            autoWidth: true,
            wrapText: true,
            enableAutoClose: true,
        })
    );
};

type HomeBannerEnableAutoRenewParams = {
    subscription: BillingDataSubscriptionInfo;
    card: ICard;
    goToSubscriptions: () => void;
};

export const dispatchEnableAutoRenewFromHomeBanner = (
    dispatch: Dispatch,
    { subscription, card, goToSubscriptions }: HomeBannerEnableAutoRenewParams
): void => {
    dispatch(
        SubscriptionRenewalActions.addAutoRenew({
            subscription,
            card,
            onSuccess: () => {
                dispatch(
                    showSnackbarAction({
                        text: getAutoRenewalEnabledFromHomeBannerSnackbarText(subscription.expires),
                        wrapText: true,
                        buttonText: 'Мои подписки',
                        onButtonClick: goToSubscriptions,
                        type: SnackbarTypes.success,
                        id: 'auto_renewal_enabled_home_banner',
                        closable: false,
                        autoWidth: true,
                        enableAutoClose: true,
                    })
                );
            },
        })
    );
};

export const dispatchEnableAutoRenewCommunication = (
    dispatch: Dispatch,
    { subscription, card, sendAnalytics }: EnableAutoRenewParams
): void => {
    const showSuccessSnackbar = (cardNumber?: string) => {
        showCommunicationSnackbar(dispatch, {
            text: cardNumber
                ? getCardAddedAndAutoRenewalEnabledSnackbarText(cardNumber, subscription.expires)
                : getAutoRenewalEnabledSnackbarText(subscription.expires),
            id: 'auto_renewal_enabled',
        });
    };

    if (card) {
        dispatch(
            SubscriptionRenewalActions.addAutoRenew({
                subscription,
                card,
                sendAnalytics,
                onSuccess: () => showSuccessSnackbar(),
            })
        );

        return;
    }

    dispatch(
        CardActions.showModal({
            showNotifications: false,
            afterUpdate: () => {
                const { card: updatedCard } = CardSelectors.getCard(store.getState());

                if (!updatedCard) {
                    return;
                }

                dispatch(
                    SubscriptionRenewalActions.addAutoRenew({
                        subscription,
                        card: updatedCard,
                        sendAnalytics,
                        onSuccess: () => showSuccessSnackbar(updatedCard.number),
                    })
                );
            },
        })
    );
};

export const dispatchAddCardCommunication = (
    dispatch: Dispatch,
    subscription: BillingDataSubscriptionInfo,
    withNextPayment = true
): void => {
    dispatch(
        CardActions.showModal({
            showNotifications: false,
            afterUpdate: () => {
                const { card: updatedCard } = CardSelectors.getCard(store.getState());

                showCommunicationSnackbar(dispatch, {
                    text: getCardAddedSnackbarText(updatedCard?.number, subscription.expires, withNextPayment),
                    id: 'unbind_card_added',
                });
            },
        })
    );
};

export const tryDispatchCommunicationCardModal = (
    dispatch: Dispatch,
    subscription: BillingDataSubscriptionInfo | undefined,
    hasCard: boolean
): boolean => {
    if (!subscription || hasCard) {
        return false;
    }

    if (autoRenewalBannerEnabled && subscription.autorenewal === false) {
        dispatchAddCardCommunication(dispatch, subscription, false);

        return true;
    }

    if (unbindCardBannerEnabled && subscription.autorenewal !== false) {
        dispatchAddCardCommunication(dispatch, subscription, true);

        return true;
    }

    return false;
};
